export const ERROR_CODE_TO_TRANSLATION = {
    'phone_already_exist_for_this_seller': 'event_page.phone_already_exist_for_this_seller',
    'end_user_season_ticket_already_in_the_same_status': 'season_ticket.dialog.end_user_season_ticket_already_in_the_same_status',
    'ticket_name_already_exists': 'free_tickets.ticket_name_already_exists',
    'end_user_has_active_season_ticket': 'end_user.delete.has_active_season_ticket',
    // 2FA error codes
    '2fa_code_expired': 'auth.2fa.code_expired',
    '2fa_code_invalid': 'auth.2fa.code_invalid',
    '2fa_no_pending': 'auth.2fa.no_pending',
    '2fa_resend_cooldown': 'auth.2fa.resend_cooldown',
    // Easy to add more mappings here:
    // 'email_already_exists': 'auth.already_exists_email',
    // 'handler_not_unique': 'marketplace.handler_already_exists',
    // Group orders (CRM, nimi-backend#989)
    'org_hp_exists': 'group_orders.orgs.hp_exists',
    'org_in_use': 'group_orders.orgs.in_use',
    'group_in_use': 'group_orders.groups.in_use',
    'group_order_not_editable': 'group_orders.orders.not_editable',
    'group_order_status_transition_invalid': 'group_orders.orders.transition_invalid',
    'group_order_slots_invalid': 'group_orders.orders.slots_invalid',
    // The dashboard's reading of "gone": the public signing page maps its own 404 through
    // useGroupOrderSign.js (status-based), so the two surfaces don't have to share a wording.
    'group_order_not_found': 'group_orders.orders.not_found',
    'sign_link_expired': 'group_orders.sign.expired_title',
    'group_order_not_sent': 'group_orders.sign.not_sent_title',
    'group_order_already_signed': 'group_orders.sign.signed_title',
    'terms_not_accepted': 'group_orders.sign.err_terms',
    'invalid_signature_image': 'group_orders.sign.err_signature_image',
    'group_order_org_email_missing': 'group_orders.orders.org_email_missing',
    'group_order_email_send_failed': 'group_orders.orders.email_send_failed',
    'group_order_event_lookup_failed': 'group_orders.orders.event_lookup_failed',
    'group_order_pdf_render_failed': 'group_orders.orders.pdf_render_failed',
    'group_order_not_signed': 'group_orders.orders.not_signed',
    // Amend after signature + admin status override (nimi-backend#1001)
    // The server verifies the ת.ז. check digit too — client validation is a courtesy, not a guarantee.
    'group_order_signer_id_invalid': 'group_orders.sign.err_id',
    'group_order_after_signature_required': 'group_orders.orders.after_signature_required',
    'group_order_status_admin_only': 'group_orders.orders.status_admin_only',
    // External voucher redemption (#801/#1236). These arrive on the ORDER/PAY path, which never
    // consulted a code map before, so the buyer/cashier saw the BE's internal English message —
    // or, when it carried none, a stringified JSON body.
    'voucher_redeem_failed_order_voided': 'event_page.voucher_redeem_failed_order_voided',
    'voucher_redeem_failed_refunded': 'event_page.voucher_redeem_failed_refunded',
    'voucher_burn_failed': 'event_page.voucher_redeem_failed_refunded',
    'voucher_not_redeemable': 'event_page.voucher_invalid',
    // A 500 caller bug (nimi-backend): nothing the seller can act on, so use the neutral copy.
    'voucher_coverage_missing': 'event_page.voucher_provider_unavailable',
    // Ticket mapping rejections (nimi-backend#1079)
    'duplicate_external_variant': 'dashboard.events.duplicate_external_variant',
    'invalid_external_variant': 'dashboard.events.invalid_external_variant',
    // Entrance analytics range cap (nimi-backend#1074). Defense-in-depth only — the backend
    // contract promises `userFacingMessage` on every error, so this fallback should rarely fire.
    'entrance_analytics_invalid_range': 'settings.entrance_analytics_range_too_long',
};

/**
 * Get API error message with priority:
 * 1. userFacingMessage from backend (if exists)
 * 2. error.message (if exists and is a string)
 * 3. Mapped translation based on error code
 * 4. General error as fallback
 *
 * @param {Error} error - Error from API call
 * @param {Function} translate - Translation function
 * @returns {string} Translated error message
 */
// The translation key for an error carrying a code we have copy for, or null to leave the caller's
// existing fallback chain alone. Lives here (not in transformers.js) so it is unit-testable:
// transformers pulls in a directory import that raw ESM cannot resolve.
export const errorCodeTranslationKey = (error) => {
    const code = error?.details?.data?.error;
    if (typeof code !== 'string') return null;
    return ERROR_CODE_TO_TRANSLATION[code] || null;
};

export const getApiErrorMessage = (error, translate) => {
    // Priority 0: 401 Unauthorized
    if (error?.details?.status === 401) {
        return translate('no_permission');
    }

    // PaymentService.preparePaymentToken/initPaymentForm reject with this top-level
    // { type, message } shape (no `.details`) when the PayMe SDK never finished initializing —
    // same mapping as getRelevantPaymentFormError (transformers.js), kept in sync here so callers
    // that go through getApiErrorMessage/getCardFormErrorMessage don't fall through to a generic
    // "general error" and lose the actionable copy.
    if (error?.type === 'payme-not-ready') {
        return translate('event_page.payme_not_ready');
    }

    const {
        userFacingMessage = '',
        message = '',
        error: errorKey
    } = error?.details?.data ?? {}

    // Priority 1: userFacingMessage from backend
    if (userFacingMessage && userFacingMessage.trim()) {
        return userFacingMessage;
    }

    // Priority 2: Check message.name for error code (nested error structure)
    if (message && typeof message === 'object' && message.name) {
        const nestedErrorKey = message.name;
        if (ERROR_CODE_TO_TRANSLATION[nestedErrorKey]) {
            return translate(ERROR_CODE_TO_TRANSLATION[nestedErrorKey]);
        }
    }

    // Priority 3: a code we have copy for beats `message`. The BE's `message` is internal English
    // ("redemption ... is not in a checkable state"), so showing it to a Hebrew-speaking seller is
    // worse than our own wording — but an UNmapped code still falls through to it below, which is
    // how every existing caller keeps behaving.
    if (errorKey && ERROR_CODE_TO_TRANSLATION[errorKey]) {
        return translate(ERROR_CODE_TO_TRANSLATION[errorKey]);
    }

    // Priority 4: error.message (if it's a meaningful string)
    if (message && typeof message === 'string' && message.trim()) {
        return message;
    }

    // Priority 5: default
    return translate('general_error');
};

/**
 * Code-first variant of getApiErrorMessage: consults ERROR_CODE_TO_TRANSLATION for a known
 * `error.details.data.error` code BEFORE falling back to the normal priority order. Needed
 * because getApiErrorMessage returns the raw BE `message` string (priority 3) before it ever
 * reaches its own code map (priority 4) — and some backends (e.g. the CRM service) always send
 * a `message`, so a mapped code would otherwise never be used.
 *
 * @param {Error} error - Error from API call
 * @param {Function} translate - Translation function
 * @returns {string} Translated error message
 */

/**
 * Dining-billing wording for codes the payment service uses generally (`charge_not_found`,
 * `charge_not_successful`, `stripe_not_connected`, …). Kept OUT of ERROR_CODE_TO_TRANSLATION so
 * only the dining screens render this copy — a subscription or basket screen hitting the same code
 * would otherwise show dining-billing text. Pass it as the third argument to
 * getApiErrorMessageCodeFirst.
 */
export const DINING_ERROR_CODE_TO_TRANSLATION = {
    'commit_already_settled': 'end_user.dining.billing.compensate_already_settled',
    'compensation_exceeds_price': 'end_user.dining.billing.compensate_exceeds_price_generic',
    'invalid_compensation_amount': 'end_user.dining.billing.compensate_amount_invalid',
    'commit_not_found': 'end_user.dining.billing.commit_not_found',
    'invalid_commit_selection': 'end_user.dining.billing.selection_stale',
    'not_a_dining_member': 'end_user.dining.billing.not_a_dining_member',
    // Manual-charge refund (payment service). Shared by the dining charge history and any other
    // manual charge — the route refunds any chargeEndUser charge, not dining ones only.
    'invalid_refund_amount': 'end_user.dining.billing.invalid_refund_amount',
    'charge_not_refundable': 'end_user.dining.billing.charge_not_refundable',
    'charge_not_successful': 'end_user.dining.billing.charge_not_successful',
    'charge_not_found': 'end_user.dining.billing.charge_not_found',
    'charge_belongs_to_another_seller': 'end_user.dining.billing.charge_belongs_to_another_seller',
    'charge_missing_refund_fields': 'end_user.dining.billing.charge_missing_refund_fields',
    'refund_sale_failed': 'end_user.dining.billing.refund_sale_failed',
    'unsupported_payment_provider': 'end_user.dining.billing.unsupported_payment_provider',
    'stripe_not_connected': 'end_user.dining.billing.stripe_not_connected',
    'missing_payme_credentials': 'end_user.dining.billing.missing_payme_credentials',
    // Payment-verified dining settle (POST /payment/charge/:accountId/:chargeId/settle-dining).
    'invalid_charge_id': 'end_user.dining.billing.invalid_charge_id',
    'charge_belongs_to_another_member': 'end_user.dining.billing.charge_belongs_to_another_member',
    'charge_is_a_refund': 'end_user.dining.billing.charge_is_a_refund',
    'charge_not_settleable': 'end_user.dining.billing.charge_not_settleable',
    'charge_was_fully_refunded': 'end_user.dining.billing.charge_was_fully_refunded',
    'charge_not_transactions_capable': 'end_user.dining.billing.charge_not_transactions_capable',
};

// Rivhit export (nimi-app#1291). Scoped like DINING_ERROR_CODE_TO_TRANSLATION so only the
// Rivhit export section renders this copy.
export const RIVHIT_ERROR_CODE_TO_TRANSLATION = {
    'rivhit_accounts_not_configured': 'nimi_invoices.rivhit_accounts_not_configured',
    'invoice_settings_not_found': 'nimi_invoices.rivhit_invoice_settings_not_found',
};

export const getApiErrorMessageCodeFirst = (error, translate, scopedMap) => {
    // Priority 0: 401 Unauthorized still wins over any mapped code (same as getApiErrorMessage).
    if (error?.details?.status === 401) {
        return translate('no_permission');
    }
    const code = error?.details?.data?.error;
    // The caller's own map wins: it carries wording scoped to that screen.
    if (code && scopedMap?.[code]) {
        return translate(scopedMap[code]);
    }
    if (code && ERROR_CODE_TO_TRANSLATION[code]) {
        return translate(ERROR_CODE_TO_TRANSLATION[code]);
    }
    return getApiErrorMessage(error, translate);
};

const PAYME_ERROR_CODE_TO_TRANSLATION = {
    'ERR-356': 'seller_public.saved_card.card_expiry_invalid',
    'ERR-21005': 'seller_public.saved_card.card_number_invalid',
};

/**
 * Error message for card-form (PayMe hosted fields) flows.
 * PayMe rejects tokenize() with its own shape — `{ type: 'tokenize-error', code, message }` for
 * server-side rejections, `{ validationError: true, errors }` for empty/malformed fields — and
 * never the API error shape getApiErrorMessage expects, so every card mistake read as a generic
 * failure. Unmapped PayMe codes still beat that fallback: the problem is always the card details.
 *
 * @param {Error} error - Error from tokenize or the save-card API call
 * @param {Function} translate - Translation function
 * @returns {string} Translated error message
 */
export const getCardFormErrorMessage = (error, translate) => {
    if (error?.type === 'tokenize-error' || error?.validationError) {
        const translationKey = PAYME_ERROR_CODE_TO_TRANSLATION[error?.code]
            || 'seller_public.saved_card.card_details_invalid';
        return translate(translationKey);
    }
    return getApiErrorMessage(error, translate);
};
